import { z } from 'zod'

type Message<TOutput> = string | z.CustomErrorParams | ((arg: TOutput) => z.CustomErrorParams)

export function notOneOf<TValues extends Array<any>, TOutput>(values: TValues, message?: Message<TOutput>) {
	function refinement(arg: TOutput, ctx: z.RefinementCtx) {
		if (!values.includes(arg)) return

		const defaultMessage = `Must not be one of: ${values.join(', ')}`

		if (typeof message === 'function') {
			const customMessage = message(arg)
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: customMessage.message ?? defaultMessage,
				...customMessage,
			})
		} else if (typeof message === 'string') {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: message ?? defaultMessage,
			})
		} else {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: message?.message ?? defaultMessage,
				...message,
			})
		}
	}

	return refinement
}

export function zodEnumFromObjKeys<K extends string>(obj: Record<K, any>): z.ZodEnum<[K, ...Array<K>]> {
	const [firstKey, ...otherKeys] = Object.keys(obj) as [K, ...Array<K>]
	return z.enum([firstKey, ...otherKeys])
}

export const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
	switch (issue.code) {
		case 'invalid_type':
			if (issue.received === 'undefined' || issue.received === 'null') return { message: `Required` }

			const friendlyTypes: Record<string, string> = {
				string: 'Text',
				number: 'Number',
				boolean: 'True/False',
				date: 'Date',
				bigint: 'Number',
				array: 'List',
				object: 'Object',
				function: 'Function',
				symbol: 'Symbol',
				map: 'Map',
				set: 'Set',
			}

			const expectedType = friendlyTypes[issue.expected] || issue.expected
			return { message: `${expectedType} is required` }

		case 'invalid_string':
			if (issue.validation === 'email') return { message: `Invalid email address` }

			if (issue.validation === 'url') return { message: `Invalid URL` }

			if (issue.validation === 'uuid') return { message: `Invalid ID format` }

			return { message: `Invalid value` }

		case 'too_small':
			if (issue.type === 'string') {
				if (issue.minimum === 1) return { message: `Required` }

				const inclusive = issue.inclusive ? 'at least' : 'more than'
				return { message: `Must be ${inclusive} ${issue.minimum} characters` }
			}
			if (issue.type === 'number') {
				const inclusive = issue.inclusive ? 'at least' : 'greater than'
				return { message: `Must be ${inclusive} ${issue.minimum}` }
			}
			if (issue.type === 'array') {
				const inclusive = issue.inclusive ? 'at least' : 'more than'
				return { message: `Must have ${inclusive} ${issue.minimum} items` }
			}
			return { message: ctx.defaultError }

		case 'too_big':
			if (issue.type === 'string') {
				const inclusive = issue.inclusive ? 'at most' : 'less than'
				return { message: `Must be ${inclusive} ${issue.maximum} characters` }
			}
			if (issue.type === 'number') {
				const inclusive = issue.inclusive ? 'at most' : 'less than'
				return { message: `Must be ${inclusive} ${issue.maximum}` }
			}
			if (issue.type === 'array') {
				const inclusive = issue.inclusive ? 'at most' : 'fewer than'
				return { message: `Must have ${inclusive} ${issue.maximum} items` }
			}
			return { message: ctx.defaultError }

		case 'invalid_date':
			return { message: `Invalid date` }

		case 'invalid_enum_value':
			return { message: `Invalid option` }

		case 'invalid_arguments':
			return { message: `Invalid arguments` }

		case 'invalid_return_type':
			return { message: `Invalid return value` }

		case 'invalid_union':
			return { message: `Invalid input` }

		case 'invalid_union_discriminator':
			return { message: `Invalid value` }

		case 'custom':
			return { message: issue.message || `Invalid value` }

		default:
			return { message: ctx.defaultError }
	}
}

export const formatErrorWithPercent = (error: string) => {
	return error.replace(/\b(\d+(\.\d+)?)\b/g, (match) => `${match}%`)
}
